import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import ListingCard from "../components/ListingCard";
import { Search, X as XIcon } from "lucide-react";

// Horses live on the dedicated /horses page (advanced filters). Marketplace is for the other categories.
const TYPES = ["all", "job", "service", "tack", "boarding"];
const TACK_TYPES = ["saddle", "bridle", "halter", "blanket", "boots", "other"];
const CONDITIONS = ["new", "like_new", "good", "used"];
const SIZE_PRESETS = ["pony", "cob", "full", "xl", "oversize"];

export default function Marketplace() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(true);
  const type = params.get("type") || "all";
  const tackType = params.get("tack_type") || "";
  const condition = params.get("condition") || "";
  const size = params.get("size") || "";

  // Redirect legacy /marketplace?type=horse|rental links to their dedicated pages
  useEffect(() => {
    if (type === "horse") {
      navigate("/horses", { replace: true });
    } else if (type === "rental") {
      navigate("/rentals", { replace: true });
    }
  }, [type, navigate]);

  useEffect(() => {
    if (type === "horse" || type === "rental") return; // handled by redirect above
    setLoading(true);
    const query = new URLSearchParams();
    if (type !== "all") query.set("type", type);
    if (q) query.set("q", q);
    if (location) query.set("location", location);
    if (type === "tack") {
      if (tackType) query.set("tack_type", tackType);
      if (condition) query.set("condition", condition);
      if (size) query.set("size", size);
    }
    api.get(`/listings?${query.toString()}`)
      .then(r => {
        // Horses & rentals have their own dedicated pages — exclude them from the "All" tab here.
        const filtered = (type === "all")
          ? r.data.filter(l => l.type !== "horse" && l.type !== "rental")
          : r.data;
        setItems(filtered);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [type, q, location, tackType, condition, size]);

  const typeLabels = useMemo(() => ({
    all: t("all"), job: t("jobs"), service: t("services"),
    tack: t("tack"), boarding: t("boarding"),
  }), [t]);

  const setParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (!value) p.delete(key); else p.set(key, value);
    setParams(p);
  };

  const clearTackFilters = () => {
    const p = new URLSearchParams(params);
    p.delete("tack_type");
    p.delete("condition");
    p.delete("size");
    setParams(p);
  };

  const activeTackFilters = (tackType ? 1 : 0) + (condition ? 1 : 0) + (size ? 1 : 0);

  return (
    <div data-testid="marketplace-page" className="pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="overline text-[#143122]">Marketplace</div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl text-[#143122]">
          {typeLabels[type] || t("all")}
        </h1>

        {/* Type + search + location */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {TYPES.map((tp) => (
            <button
              key={tp}
              data-testid={`filter-${tp}`}
              onClick={() => {
                const p = new URLSearchParams(params);
                if (tp === "all") p.delete("type"); else p.set("type", tp);
                // reset tack-specific filters when switching away from tack
                if (tp !== "tack") {
                  p.delete("tack_type");
                  p.delete("condition");
                  p.delete("size");
                }
                setParams(p);
              }}
              className={`kav-chip ${type === tp ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
            >
              {typeLabels[tp]}
            </button>
          ))}
          <div className="flex-1 min-w-[220px] relative">
            <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1A1F1C]/50" />
            <input
              data-testid="search-input"
              className="kav-input pl-9"
              placeholder={t("search_placeholder")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <input
            data-testid="location-input"
            className="kav-input max-w-[180px]"
            placeholder={t("filter_location")}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>

        {/* Tack-specific filters (only shown when type=tack) */}
        {type === "tack" && (
          <div
            data-testid="tack-filters"
            className="mt-4 rounded-lg border border-[#E6E2D8] bg-[#FAFAFA] p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-[#143122]">
                {t("refine_tack")}
                {activeTackFilters > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-[#143122] text-white text-[10px]">
                    {activeTackFilters}
                  </span>
                )}
              </div>
              {activeTackFilters > 0 && (
                <button
                  type="button"
                  data-testid="tack-clear-btn"
                  onClick={clearTackFilters}
                  className="text-xs text-[#143122] hover:underline inline-flex items-center gap-1"
                >
                  <XIcon size={12} strokeWidth={1.75} /> {t("clear_filters")}
                </button>
              )}
            </div>

            {/* Tack type chips */}
            <div className="mb-3">
              <div className="text-xs font-medium mb-1.5 text-[#1A1F1C]/70">{t("tack_type")}</div>
              <div className="flex flex-wrap gap-2">
                {TACK_TYPES.map((tt) => (
                  <button
                    key={tt}
                    type="button"
                    data-testid={`tack-type-${tt}`}
                    onClick={() => setParam("tack_type", tackType === tt ? "" : tt)}
                    className={`kav-chip text-xs ${tackType === tt ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
                  >
                    {t(`tack_${tt}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Condition + size row */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-medium mb-1.5 text-[#1A1F1C]/70">{t("condition")}</div>
                <select
                  data-testid="tack-condition-select"
                  className="kav-input"
                  value={condition}
                  onChange={(e) => setParam("condition", e.target.value)}
                >
                  <option value="">{t("any")}</option>
                  {CONDITIONS.map((c) => (
                    <option key={c} value={c}>{t(`condition_${c}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-xs font-medium mb-1.5 text-[#1A1F1C]/70">{t("size")}</div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {SIZE_PRESETS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`tack-size-${s}`}
                      onClick={() => setParam("size", size === s ? "" : s)}
                      className={`kav-chip text-xs ${size === s ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
                    >
                      {t(`size_${s}`)}
                    </button>
                  ))}
                </div>
                <input
                  data-testid="tack-size-input"
                  className="kav-input text-sm"
                  placeholder={t("size_custom_placeholder")}
                  value={SIZE_PRESETS.includes(size) ? "" : size}
                  onChange={(e) => setParam("size", e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        <div className="mt-10">
          {loading ? (
            <div className="text-sm text-[#1A1F1C]/50">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-16 text-center">
              {t("no_listings_match")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {items.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
